import { passClick } from '../lib/mapPass';
import { mPerLon } from '../lib/geo';
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { computePit, pitRing, pitCenter, PIT_COLOR, rectFromCorners, rectFromParams, rectDims, offsetM, moveBy, rotatePts, polyArea } from '../lib/excavation';
import { haversine } from '../lib/model';
import { MAINT, maintMatch } from '../lib/maint';

const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const f2 = (n) => n.toFixed(2).replace('.', ',');
const handle = (ll, cls, testid) => L.marker(ll, { draggable: true, zIndexOffset: 1200, icon: L.divIcon({ className: 'pit-handle-wrap', html: `<span class="pit-handle ${cls}" data-testid="${testid}"></span>`, iconSize: [22, 22], iconAnchor: [11, 11] }) });

function draftLabel(d) {
  const [a, b] = d.points;
  if (d.shape === 'rect' && b) { const { len, wid } = rectDims(rectFromCorners([a, b])); return `${f2(len)} × ${f2(wid)} m = ${f2(len * wid)} m²`; }
  if (d.shape === 'circle' && b) { const r = haversine(a, b); return `Ø ${f2(2 * r)} m = ${f2(Math.PI * r * r)} m²`; }
  if (d.shape === 'polygon' && d.points.length > 2) return `${f2(polyArea(d.points))} m²`;
  return '';
}

function drawDraft(g, d) {
  const pts = d.points;
  const style = { pane: 'pits', color: PIT_COLOR, weight: 2.5, dashArray: '6 5', fillColor: PIT_COLOR, fillOpacity: 0.15, interactive: false };
  pts.forEach((p) => g.addLayer(L.circleMarker(p, { pane: 'pits', radius: 5, color: '#fff', weight: 2, fillColor: PIT_COLOR, fillOpacity: 1, interactive: false })));
  if (d.shape === 'polygon' && pts.length > 1) g.addLayer(pts.length > 2 ? L.polygon(pts, style) : L.polyline(pts, style));
  if (d.shape === 'rect' && pts.length === 2) g.addLayer(L.polygon(rectFromCorners(pts), style));
  if (d.shape === 'circle' && pts.length === 2) g.addLayer(L.circle(pts[0], { ...style, radius: haversine(pts[0], pts[1]) }));
  const lab = draftLabel(d);
  if (lab) g.addLayer(L.marker(pts[pts.length - 1], { interactive: false, icon: L.divIcon({ className: 'pit-live', html: `<span data-testid="pit-live-label">${lab}</span>`, iconSize: [0, 0] }) }));
}

const centerOf = (p) => (p.shape === 'circle' ? p.center : p.shape === 'rect' && p.center ? p.center : pitCenter(p));

// geometry patch for a dragged handle
function editPatch(p, kind, ll, i) {
  const c = centerOf(p);
  if (kind === 'move') {
    const dLat = ll[0] - c[0], dLon = ll[1] - c[1];
    if (p.shape === 'circle') return { center: ll };
    return p.shape === 'rect' ? { center: ll, pts: moveBy(p.pts, dLat, dLon) } : { pts: moveBy(p.pts, dLat, dLon) };
  }
  if (p.shape === 'circle') return { r: Math.max(0.1, haversine(c, ll)) };
  if (kind === 'rot') {
    const [x, y] = offsetM(c, ll);
    const rot = (Math.atan2(y, x) * 180) / Math.PI - 90;
    if (p.shape === 'rect') return { rot, pts: rectFromParams(c, p.len, p.wid, rot) };
    return { pts: rotatePts(p.pts, c, rot - (p.rot || 0)), rot };
  }
  if (p.shape === 'rect') {
    const [x, y] = offsetM(c, ll);
    const a = ((p.rot || 0) * Math.PI) / 180;
    const len = Math.max(0.2, 2 * Math.abs(x * Math.cos(a) + y * Math.sin(a)));
    const wid = Math.max(0.2, 2 * Math.abs(-x * Math.sin(a) + y * Math.cos(a)));
    return { len, wid, areaOverride: null, pts: rectFromParams(c, len, wid, p.rot || 0) };
  }
  const pts = p.pts.map((q, k) => (k === i ? ll : q));
  return { pts, areaOverride: null };
}

function addHandles(g, p, onChange, refresh) {
  let cur = { ...p };
  const c = centerOf(p);
  const live = (kind, i) => (e) => { const ll = e.target.getLatLng(); cur = { ...cur, ...editPatch(cur, kind, [ll.lat, ll.lng], i) }; refresh(cur); };
  const end = () => onChange(p.id, cur);
  const mk = (ll, kind, cls, i = 0) => { const m = handle(ll, cls, `pit-handle-${kind}${kind === 'corner' ? '-' + i : ''}`); m.on('drag', live(kind, i)); m.on('dragend', end); g.addLayer(m); };
  mk(c, 'move', 'move');
  if (p.shape === 'circle') { mk([c[0], c[1] + p.r / (mPerLon(c[0]))], 'edge', 'edge'); return; }
  p.pts.forEach((q, i) => mk(q, 'corner', 'corner', i));
  const top = [(p.pts[2][0] + p.pts[3 % p.pts.length][0]) / 2, (p.pts[2][1] + p.pts[3 % p.pts.length][1]) / 2];
  mk([top[0] + (top[0] - c[0]) * 0.6, top[1] + (top[1] - c[1]) * 0.6], 'rot', 'rot');
}

function useDragDraw(mapRef, draft, setDraft) {
  const d = useRef(draft);
  d.current = draft;
  const active = draft?.kind === 'pit' && draft.shape !== 'polygon';
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !active) return undefined;
    const el = map.getContainer();
    map.dragging.disable();
    let start = null, moved = false;
    const ll = (e) => { const p = map.mouseEventToLatLng(e); return [p.lat, p.lng]; };
    const down = (e) => { if (e.target.closest('.leaflet-control, .leaflet-marker-icon')) return; start = { ll: ll(e), x: e.clientX, y: e.clientY }; moved = false; };
    const move = (e) => {
      if (!start) return;
      if (!moved && Math.hypot(e.clientX - start.x, e.clientY - start.y) < 8) return;
      moved = true;
      setDraft({ ...d.current, points: [start.ll, ll(e)], dragging: true });
    };
    const up = (e) => {
      if (start && moved) {
        const stop = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        el.addEventListener('click', stop, { capture: true, once: true });
        setTimeout(() => el.removeEventListener('click', stop, { capture: true }), 400);
        setDraft({ ...d.current, points: [start.ll, ll(e)], dragging: false });
      }
      start = null;
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { el.removeEventListener('pointerdown', down); el.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); map.dragging.enable(); };
  }, [mapRef, active, setDraft]);
}

export default function usePitLayers(mapRef, { pits = [], draft, onPitClick, t, selId, onPitChange, setDraft, maintFilter }) {
  const groupRef = useRef(null);
  const cb = useRef({});
  cb.current = { onPitClick, onPitChange };
  useDragDraw(mapRef, draft, setDraft);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!groupRef.current || groupRef.current.__map !== map) {
      groupRef.current = L.layerGroup().addTo(map);
      groupRef.current.__map = map;
    }
    const g = groupRef.current;
    g.clearLayers();
    pits.forEach((p) => {
      const c = computePit(p);
      const sel = p.id === selId && !draft;
      const mt = p.maint?.status, dim = !maintMatch(maintFilter, p);
      const poly = L.polygon(pitRing(p), { pane: 'pits', color: sel ? '#fde047' : mt ? MAINT[mt] : '#c2410c', weight: sel ? 3.5 : mt ? 4 : 2.5, opacity: dim ? 0.25 : 1, fillColor: PIT_COLOR, fillOpacity: dim ? 0.08 : 0.35, className: `pit-shape pit-${p.id}`, interactive: !draft });
      poly.bindTooltip(`${esc(p.name)} · ${f2(c.area)} m² · ${f2(c.excav.m3)} m³`, { sticky: true });
      poly.on('click', (e) => { L.DomEvent.stopPropagation(e); if (!window.__gissDrafting) cb.current.onPitClick?.(p.id); else passClick(e); });
      g.addLayer(poly);
      const label = L.marker(pitCenter(p), { pane: 'pits', interactive: false, icon: L.divIcon({ className: 'pit-label', html: `<span>${esc(p.name)}<b>${c.excav.m3.toFixed(1).replace('.', ',')} m³</b></span>`, iconSize: [0, 0] }) });
      g.addLayer(label);
      if (sel) {
        addHandles(g, p, (id, next) => cb.current.onPitChange?.(id, next), (cur) => {
          poly.setLatLngs(pitRing(cur));
          const cc = computePit(cur);
          label.setLatLng(pitCenter(cur));
          label.setIcon(L.divIcon({ className: 'pit-label', html: `<span>${f2(cc.area)} m²<b>${cc.excav.m3.toFixed(1).replace('.', ',')} m³</b></span>`, iconSize: [0, 0] }));
        });
      }
    });
    if (draft?.kind === 'pit') drawDraft(g, draft);
  }, [mapRef, pits, draft, t, selId, maintFilter]);
}
